import { fromBaseRecord, type FromBaseRow as Row } from '../compile/from-base-context';
import { heldMemberDefinitionInputs } from '../compile/member-status-inputs';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField } from '../compile/inventory-raw-readers';
import { fullDefinition } from '../reactions/php-row-rules';
import type { AppliedFrameResult } from './applied-frame';

/** Observers may never have seen the definitions an action applies. Encode only
 * those held closures, with separate raw compiler inputs and compiled display rows. */
export function appliedDefinitionIds(frame: AppliedFrameResult): Set<string> {
    if ('cache_ref' in frame) return new Set();
    // Existing carried inventory rows are held state. Only births need template donors.
    const values = [...frame.rows.filter(row => row.table === 'statuses').map(row => readItemField(row.row, 'status_item_id')),
        ...(frame.new_inventory ?? []).flatMap(birth => birth.base.map(row => readInventoryField(row, 'inventory_item_id')))];
    return new Set(values.filter((id): id is string => typeof id === 'string' && id !== '').map(id => id.toUpperCase()));
}
export function withAppliedDefinitionInputs(frame: AppliedFrameResult,
    raw: (id: string) => Row | undefined, compiled: (id: string) => Row | undefined, shared: Row = {},
    ids = appliedDefinitionIds(frame)): AppliedFrameResult {
    if ('cache_ref' in frame) return frame;
    if (!ids.size) return frame;
    let inputs: Row = {};
    const births = frame.new_inventory ?? [], owned = new WeakSet<object>();
    const sources = births.map(birth => birth.definition_inputs).filter(fromBaseRecord);
    const catalogs = births.map(birth => birth.definition_catalog)
        .filter((catalog): catalog is Readonly<Record<string, Row>> => fromBaseRecord(catalog));
    for (const id of ids) {
        const source = sources.find(source => fromBaseRecord(source.catalog) && fromBaseRecord(source.catalog[id])) ?? raw(id);
        inputs = heldMemberDefinitionInputs(inputs, id, source, shared, owned) ?? inputs;
    }
    const catalog = fromBaseRecord(inputs.catalog) ? inputs.catalog : {};
    const display: Row = {};
    for (const id of new Set([...ids, ...Object.keys(catalog)])) {
        const row = catalogs.map(catalog => catalog[id]).find(fromBaseRecord) ?? fullDefinition(compiled(id) ?? null, 'any');
        if (row) display[id] = row;
    }
    const { catalog: _catalog, ...witnesses } = inputs;
    return { ...frame, inputs: { ...frame.inputs,
        catalog: { ...fromBaseRecord(frame.inputs?.catalog) ? frame.inputs.catalog : {}, ...catalog },
        ...(Object.keys(witnesses).length ? { definition_inputs: witnesses } : {}), definition_catalog: display },
        ...(frame.new_inventory ? { new_inventory: births.map(birth => {
            const { definition_inputs: _inputs, definition_catalog: _definitions, ...rows } = birth;
            return rows;
        }) } : {}) };
}
